import { CSSProperties, ReactElement } from 'react'
import { SmartTooltip } from '../../../components/SmartTooltip/SmartTooltip'
import { IMyLedgerBalances, LedgerItemStatus } from './core/types'

const STATUS_LABEL: Record<LedgerItemStatus, string> = {
  ok: '',
  not_found: 'нет кошелька',
  unavailable: 'недоступно',
}

const numCell: CSSProperties = {
  padding: '5px 10px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
}
const textCell: CSSProperties = { padding: '5px 10px', textAlign: 'left', whiteSpace: 'nowrap' }
const rowBorder: CSSProperties = { borderBottom: '1px solid var(--bs-border-color)' }

/** Amount + its currency — the currency must stay glued to the number, otherwise
 *  mixed-currency rows read as if they were the same money. */
function Amount({ value, currency }: { value: string | null; currency: string | null }) {
  return (
    <>
      {value}
      {currency && (
        <span style={{ color: 'var(--bs-secondary-color)', marginLeft: 4 }}>{currency}</span>
      )}
    </>
  )
}

export function LedgerBalancePopover({
  data,
  children,
}: {
  data: IMyLedgerBalances
  children: ReactElement
}) {
  const isMerchant = data.ownerType === 'merchant'
  const ownerCol = isMerchant ? 'Мерчант' : 'Касса'
  const title = isMerchant ? 'Баланс по мерчантам' : 'Баланс по кассам'

  const card = (
    <div
      style={{
        backgroundColor: 'var(--bs-body-bg)',
        color: 'var(--bs-body-color)',
        border: '1px solid var(--bs-border-color)',
        borderRadius: 8,
        boxShadow: '0 4px 16px rgba(0,0,0,.18)',
        padding: '10px 12px',
        minWidth: 460,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>{title}</div>
      <table
        style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, color: 'var(--bs-body-color)' }}
      >
        <thead>
          <tr style={{ ...rowBorder, color: 'var(--bs-secondary-color)' }}>
            <th style={{ ...textCell, fontWeight: 600 }}>{ownerCol}</th>
            <th style={{ ...numCell, fontWeight: 600 }}>Баланс</th>
            <th style={{ ...numCell, fontWeight: 600 }}>Кредит</th>
            <th style={{ ...numCell, fontWeight: 600 }}>Холд</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((it) => (
            <tr key={it.id} style={{ ...rowBorder, opacity: it.status === 'ok' ? 1 : 0.55 }}>
              <td style={textCell}>
                {it.name}
                {it.isBlocked && (
                  <span style={{ color: 'var(--bs-danger)', marginLeft: 6 }} title='Кошелёк заблокирован'>
                    ●
                  </span>
                )}
              </td>
              {it.status === 'ok' ? (
                <>
                  <td style={numCell}>
                    <Amount value={it.availableBalance} currency={it.currency} />
                  </td>
                  <td style={numCell}>{it.creditBalance}</td>
                  <td style={numCell}>{it.debitsPending}</td>
                </>
              ) : (
                <td style={{ ...textCell, color: 'var(--bs-secondary-color)' }} colSpan={3}>
                  {STATUS_LABEL[it.status]}
                </td>
              )}
            </tr>
          ))}
        </tbody>
        {data.summary.length > 0 && (
          <tfoot>
            {data.summary.map((s) => (
              <tr key={s.currency} style={{ fontWeight: 700 }}>
                <td style={textCell}>Итого {s.currency}</td>
                <td style={numCell}>
                  <Amount value={s.availableBalance} currency={s.currency} />
                </td>
                <td style={numCell}>{s.creditBalance}</td>
                <td style={numCell}>{s.debitsPending}</td>
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  )

  return (
    <SmartTooltip content={card} bare>
      {children}
    </SmartTooltip>
  )
}
